import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function SEO({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  keywords,
  author,
  publishedTime,
  modifiedTime,
  schema,
  noIndex = false,
  robots = null, // explicit robots override, e.g. 'noindex, follow'
  additionalSchema = null, // extra JSON-LD blocks, emitted alongside the default organization schema
  breadcrumbs = null // Array of { name, url } for BreadcrumbList schema
}) {
  const fullTitle = title || 'Clinivo - Healthcare Jobs & Nursing Careers | Find Your Next Role';
  const desc = description || 'Connect with top healthcare facilities and discover your next career opportunity. Find nursing, physician, therapy, and medical jobs nationwide.';
  const imageUrl = image ||     'https://media.base44.com/images/public/696ac43216f2dcd8c8bf25f9/3c96e018a_generated_image.png';
  // Build a clean canonical URL: always use clinivo.net, strip any fragment, use provided url or current path+search
  // Always use a clean canonical: strip trailing slash (except root), strip fragments, no query params unless explicitly provided
  const siteUrl = (() => {
    const base = url || (typeof window !== 'undefined'
      ? `https://clinivo.net${window.location.pathname}`
      : 'https://clinivo.net');
    // Remove trailing slash unless it's the root
    return base.endsWith('/') && base !== 'https://clinivo.net/' 
      ? base.slice(0, -1) 
      : base;
  })();
  const defaultKeywords = 'healthcare jobs, nursing jobs, travel nursing jobs, physician jobs, medical careers, healthcare careers, hospital jobs, clinical jobs, RN jobs, LPN jobs, allied health jobs';

  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Clinivo", "item": "https://clinivo.net" },
      ...breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        "position": i + 2,
        "name": b.name,
        "item": b.url
      }))
    ]
  } : null;

  // Default Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Clinivo",
    "description": "Healthcare job board connecting professionals with top facilities",
    "url": "https://clinivo.net",
    "logo": "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/696ac43216f2dcd8c8bf25f9/af21d9783_Clinivologo.PNG",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": "English"
    }
  };

  // An explicit `robots` override must govern the googlebot directive too —
  // otherwise a page served 'noindex, follow' would still tell Googlebot
  // 'index, follow', and the googlebot directive wins for Google.
  const robotsValue = robots || (noIndex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  const robotsBlocked = /noindex/i.test(robotsValue);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      {author && <meta name="author" content={author} />}
      <link rel="canonical" href={siteUrl} />
      <meta name="robots" content={robotsValue} />
      <meta name="googlebot" content={robotsBlocked ? 'noindex, follow' : 'index, follow'} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Clinivo" />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Additional SEO */}
      <meta name="theme-color" content="#0d9488" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schema || organizationSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {Array.isArray(additionalSchema) && additionalSchema.map((entry, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(entry)}
        </script>
      ))}
    </Helmet>
  );
}